/*
  Copyright Epic Games, Inc. All Rights Reserved.
*/

import { LatestNewsSectionFragment } from "@cosmos/hygraph-schema";

import { useCosmosComponentContext } from "@components/context";
import {
	BackgroundContainer,
	FeedCardOrganism,
	FeedItem,
	ResponsiveContainer,
	SectionContainer,
	SectionHeading,
} from "@components/elements";
import { extractBackgroundContainerProps } from "@components/utils/section.utils";
import { useLocale } from "@epic-mw/i18n";
import { css, cx } from "@panda/css";

/**
 * The featured card and the card grid bring their own section containers, so the heading only keeps
 * its block-start padding and this override tightens the gap between the heading and the cards.
 */
const headingSpacingStyles = css({
	"&& .feed-card-organism .section-container.padding-block-start": {
		paddingBlockStart: "eds.dimension.48",
		smDown: { paddingBlockStart: "eds.dimension.40" },
	},
});

export interface MediumFeaturedSectionProps extends Pick<
	LatestNewsSectionFragment,
	"id" | "config" | "featuredLabel" | "latestNewsVariant"
> {
	items?: FeedItem[] | null;
	feedBrand?: string | null;
	hideCategoryPills?: boolean;
	/** True when a page hero renders above this section — forwarded to the featured card so it stays an h2. */
	hasHero?: boolean;
	className?: string;
}

/**
 * Medium featured variants of the LatestNews section: a prominent featured card followed by a blog
 * card grid, wrapped in the section config (background, anchor, padding) and optional heading.
 *
 * The variant drives both the card count (4 or 10) and the featured card orientation (flip).
 */
export const MediumFeaturedSection = ({
	config,
	items,
	latestNewsVariant,
	featuredLabel,
	feedBrand,
	hideCategoryPills,
	hasHero,
	className,
}: MediumFeaturedSectionProps): React.JSX.Element | null => {
	const locale = useLocale();
	const { feedBrand: contextFeedBrand, locales } = useCosmosComponentContext();

	const cleanVariant = latestNewsVariant?.toLowerCase();
	const maxItems = cleanVariant?.includes("10") ? 10 : 4;
	const visibleItems = items?.slice(0, maxItems) ?? [];

	const validHeading = Boolean(config?.heading?.title || config?.heading?.desc);
	const validContent = Boolean(visibleItems.length);
	if (!validHeading && !validContent) return null;

	return (
		<BackgroundContainer {...extractBackgroundContainerProps(config)}>
			<SectionContainer
				anchorId={config?.anchorId}
				disablePaddingBlock
				disablePaddingInline
				className={cx("latest-news-medium-featured-section", validHeading && headingSpacingStyles, className)}
			>
				{validHeading ? (
					<ResponsiveContainer>
						<SectionContainer disablePaddingBlockEnd>
							<SectionHeading {...config?.heading} />
						</SectionContainer>
					</ResponsiveContainer>
				) : null}
				{validContent ? (
					<FeedCardOrganism
						locale={locale}
						locales={locales}
						items={visibleItems}
						featuredLabel={featuredLabel}
						enableAnimate={false}
						flipFeatured={cleanVariant?.includes("flip")}
						currentBrand={feedBrand ?? contextFeedBrand}
						hideCategoryPills={hideCategoryPills}
						// A section heading above owns the higher heading level, so the featured card stays an h2.
						hasHero={hasHero || validHeading}
						disablePaddingBlockEnd={Boolean(config?.disablePaddingBottom)}
					/>
				) : null}
			</SectionContainer>
		</BackgroundContainer>
	);
};
